import { Mesh, MeshStandardMaterial, type Object3D } from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { allModelAssets, ModelAsset, modelAssetUrls } from "./model-assets";
import { cloneSharedModel, markSharedModelResources } from "./shared-model-resources";

const rackStructureAssets = new Set<ModelAsset>([
  ModelAsset.PalletStackerCraneGroundRail,
  ModelAsset.PalletStackerCraneBase,
  ModelAsset.PalletStackerCranePost,
  ModelAsset.PalletStackerCraneTop,
  ModelAsset.PalletStackerCraneSkyRail,
  ModelAsset.PalletStackerCraneCarriage,
  ModelAsset.PalletStackerCraneControlCabinet,
  ModelAsset.AngleIron,
  ModelAsset.BasePlate,
  ModelAsset.BinRackBrace,
  ModelAsset.BinRackCrossbar,
  ModelAsset.BinRackPanel,
  ModelAsset.BinRackPost,
  ModelAsset.Crossbar,
  ModelAsset.DoubleChainConveyorCrossbar,
  ModelAsset.DoubleChainConveyorPost,
  ModelAsset.DoubleChainConveyorSideRail,
  ModelAsset.DoubleChainConveyorWheel,
  ModelAsset.PalletReciprocatingLiftBase,
  ModelAsset.PalletReciprocatingLiftBottomSideRail,
  ModelAsset.PalletReciprocatingLiftLayerBeam,
  ModelAsset.PalletReciprocatingLiftMovingAssembly,
  ModelAsset.PalletReciprocatingLiftPost,
  ModelAsset.PalletReciprocatingLiftTop,
  ModelAsset.RackPost,
  ModelAsset.RollerConveyorCrossbar,
  ModelAsset.RollerConveyorPost,
  ModelAsset.RollerConveyorRoller,
  ModelAsset.RollerConveyorSideRail,
  ModelAsset.RollerLiftTransferCrossbar,
  ModelAsset.RollerLiftTransferRoller,
  ModelAsset.SideBrace,
  ModelAsset.SingleStationReciprocatingShuttleBody,
  ModelAsset.SingleStationReciprocatingShuttleRail,
  ModelAsset.SingleStationReciprocatingShuttleTop
]);

export interface ModelAssetLoader {
  load(asset: ModelAsset): Promise<Object3D>;
}

export class ModelAssetLibrary implements ModelAssetLoader {
  private readonly loader = new GLTFLoader();
  private readonly templates = new Map<ModelAsset, Promise<Object3D>>();

  preload(assets: ModelAsset[] = allModelAssets) {
    return Promise.all(assets.map((asset) => this.loadTemplate(asset))).then(() => undefined);
  }

  async load(asset: ModelAsset) {
    return cloneSharedModel(await this.loadTemplate(asset));
  }

  private loadTemplate(asset: ModelAsset) {
    const cachedTemplate = this.templates.get(asset);

    if (cachedTemplate) {
      return cachedTemplate;
    }

    const template = this.loader.loadAsync(modelAssetUrls[asset]).then((gltf) => {
      applyMaterialProfile(gltf.scene, asset);
      markSharedModelResources(gltf.scene);
      return gltf.scene;
    });
    this.templates.set(asset, template);
    return template;
  }
}

export const modelAssetLibrary = new ModelAssetLibrary();

function applyMaterialProfile(object: Object3D, asset: ModelAsset) {
  if (!rackStructureAssets.has(asset)) {
    return;
  }

  object.traverse((child) => {
    if (!(child instanceof Mesh)) {
      return;
    }
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material) => {
      if (!(material instanceof MeshStandardMaterial)) {
        return;
      }
      material.metalness = 0.48;
      material.roughness = 0.18;
      material.envMapIntensity = 1.8;
    });
  });
}
